import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/AuthContext'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'

interface SignInProps {
  onSwitchToSignUp: () => void
  onSwitchToForgotPassword: () => void
}

const SignIn: React.FC<SignInProps> = ({ onSwitchToSignUp, onSwitchToForgotPassword }) => {
  const { t } = useTranslation('auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { signIn } = useAuth()
  const navigate = useLocalizedNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    console.log('Attempting to sign in with:', { email, password: '***' })

    try {
      const { error } = await signIn(email, password)
      
      if (error) {
        console.error('Sign in error:', error)
        setError(error.message)
      } else {
        console.log('Sign in successful, redirecting...')
        // Redirect to home page after successful sign in
        navigate('/')
      }
    } catch (err) {
      console.error('Unexpected error during sign in:', err)
      setError(t('errors.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('signIn.title')}</h1>
          <p className="text-sm sm:text-base text-gray-600">{t('signIn.subtitle')}</p>
        </div>

        {/* Sign In Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('signIn.emailLabel')}
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('signIn.emailPlaceholder')}
              required
              className="w-full"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('signIn.passwordLabel')}
              </label>
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('signIn.forgotPasswordLink')}
              </button>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('signIn.passwordPlaceholder')}
              required
              className="w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300"
          >
            {loading ? t('signIn.submitting') : t('signIn.submitButton')}
          </Button>
        </form>

        {/* Switch to Sign Up */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            {t('signIn.noAccount')}{' '}
            <button
              onClick={onSwitchToSignUp}
              className="text-gray-800 font-medium hover:text-gray-900 transition-colors"
            >
              {t('signIn.signUpLink')}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default SignIn
